import { ArrowLeft, ExternalLink, RotateCcw } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

export interface ActionContext {
  router: AppRouterInstance
  pathname: string
}

type ActionHandlerFactory = (ctx: ActionContext, payload?: Record<string, string>) => () => void

const handlers = new Map<string, ActionHandlerFactory>()
const icons = new Map<string, LucideIcon>()

/**
 * Register a notification action handler. Call at module scope (not inside components).
 * Features register their own action types — NotificationBell resolves them at render time.
 *
 * @example
 * registerAction('navigate-to-deal', (ctx, payload) => () => ctx.router.push(`/deals/${payload?.id}`), Eye)
 */
export function registerAction(actionType: string, handler: ActionHandlerFactory, icon?: LucideIcon) {
  handlers.set(actionType, handler)
  if (icon) icons.set(actionType, icon)
}

export function resolveAction(
  actionType: string,
  ctx: ActionContext,
  payload?: Record<string, string>,
): (() => void) | undefined {
  return handlers.get(actionType)?.(ctx, payload)
}

export function getActionIcon(actionType: string): LucideIcon | undefined {
  return icons.get(actionType)
}

// --- Built-in actions ---

registerAction('reset-filters', (ctx) => () => ctx.router.replace(ctx.pathname, { scroll: false }), RotateCcw)
registerAction('navigate-back', (ctx) => () => ctx.router.back(), ArrowLeft)
registerAction('open-url', (_ctx, payload) => () => window.open(payload?.url, '_blank'), ExternalLink)
